import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useSchedule } from '../../contexts/schedule';
import { useScheduleOperations } from '../../contexts/schedule/ScheduleOperationsContext';
import { useStaff } from '../../contexts/StaffContext';
import { useDepartment } from '../../contexts/DepartmentContext';
import { useNotification } from '../../contexts/NotificationContext.js';
import ScheduleService from '../../services/ScheduleService';
import { ScheduleUtils } from '../../models/ScheduleModels';
import ScheduleSelectionPanel from '../../components/schedule/ScheduleSelectionPanel';
import MonthlyCalendarView from '../../components/schedule/MonthlyCalendarView';
import { RepeatSection } from '../../components/schedule/RepeatSection';
import DepartmentOverviewCalendar from '../../components/schedule/DepartmentOverviewCalendar';
import '../../styles/schedule-planner-layout.css';
import '../../styles/schedule-planner-components.css';
import '../../styles/schedule-planner-interactions.css';

import {
    query,
    where,
    getDocs
} from 'firebase/firestore';

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

const START_HOUR = 7;
const END_HOUR = 19;
const TIME_SLOT_MINUTES = 15;
const SLOTS_PER_HOUR = 4;
const TOTAL_SLOTS = ((END_HOUR - START_HOUR) + 1) * SLOTS_PER_HOUR;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const generateTimeSlots = () => {
    const slots = [];
    for (let hour = START_HOUR; hour <= END_HOUR; hour++) {
        for (let minute = 0; minute < 60; minute += TIME_SLOT_MINUTES) {
            if (hour === END_HOUR && minute > 0) break;

            const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
            slots.push({
                hour,
                minute,
                timeString,
                slotIndex: slots.length
            });
        }
    }
    return slots;
};

const getDayNames = (t) => [
    t('days.long.mon'),
    t('days.long.tue'),
    t('days.long.wed'),
    t('days.long.thu'),
    t('days.long.fri'),
    t('days.long.sat'),
    t('days.long.sun'),
];

const formatDuration = (totalMinutes) => {
    if (totalMinutes < 60) {
        return `${totalMinutes} min`;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (minutes === 0) {
        return `${hours} h`;
    }

    return `${hours} h ${minutes} min`;
};

const convertTimeToMinutes = (time) => {
    if (!time) return 0;

    if (time.toDate && typeof time.toDate === 'function') {
        const date = time.toDate();
        return date.getHours() * 60 + date.getMinutes();
    }

    if (typeof time === 'string') {
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
    }

    if (typeof time === 'number') {
        return time;
    }

    console.warn('Unknown time format:', time);
    return 0;
};

const convertMinutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

const generateSelectionId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

const convertShiftsToSelections = (shifts) => {
    if (!shifts || shifts.length === 0) return [];

    return shifts.map(shift => {
        let startTime, endTime, duration;

        if (shift.startTime && shift.endTime) {
            const startTimeStr = typeof shift.startTime === 'string'
                ? shift.startTime
                : convertMinutesToTime(convertTimeToMinutes(shift.startTime));
            const endTimeStr = typeof shift.endTime === 'string'
                ? shift.endTime
                : convertMinutesToTime(convertTimeToMinutes(shift.endTime));

            startTime = startTimeStr;
            endTime = endTimeStr;
            duration = convertTimeToMinutes(endTimeStr) - convertTimeToMinutes(startTimeStr);
        } else if (typeof shift.startHour === 'number' && typeof shift.startMinute === 'number') {
            const startMinutes = shift.startHour * 60 + shift.startMinute;
            const endMinutes = shift.endHour * 60 + shift.endMinute;

            startTime = convertMinutesToTime(startMinutes);
            endTime = convertMinutesToTime(endMinutes);
            duration = endMinutes - startMinutes;
        } else if (shift.durationMinutes && shift.startTime) {
            startTime = typeof shift.startTime === 'string'
                ? shift.startTime
                : convertMinutesToTime(convertTimeToMinutes(shift.startTime));
            duration = shift.durationMinutes;
            endTime = convertMinutesToTime(convertTimeToMinutes(startTime) + duration);
        } else {
            console.warn('Unknown shift format, skipping:', shift);
            return null;
        }

        const selection = {
            id: shift.id || `firebase-${shift.date}-${generateSelectionId()}`,
            day: shift.date,
            startTime: startTime,
            endTime: endTime,
            duration: duration,
            isSaved: true,
            batchId: shift.batchId || null,
            recurrence: shift.recurrence || null,
            repeatEnd: shift.repeatEnd || null,
            shiftId: shift.id
        };

        return selection;
    }).filter(Boolean);
};

// ============================================================================
// EDIT TIME MODAL COMPONENT
// ============================================================================

const TimeEditModal = ({
    isOpen,
    onClose,
    dateISO,
    startTime,
    endTime,
    blockId,
    onSave,
    onDelete,
    staffId,
    companyId,
    currentUserId,
    onRefresh,
    isNew,
    departmentId
}) => {
    const { t } = useTranslation('schedule');

    const [editDate, setEditDate] = useState(dateISO || new Date().toISOString().split('T')[0]);
    const [editStartTime, setEditStartTime] = useState(startTime || '09:00');
    const [editEndTime, setEditEndTime] = useState(endTime || '17:00');
    const [error, setError] = useState('');
    const { showNotification } = useNotification();

    const [repeatConfig, setRepeatConfig] = useState(null);
    const [showReplaceDialog, setShowReplaceDialog] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setEditDate(dateISO || new Date().toISOString().split('T')[0]);
            setEditStartTime(startTime || '09:00');
            setEditEndTime(endTime || '17:00');
            setError('');
            setRepeatConfig(null);
        }
    }, [isOpen, dateISO, startTime, endTime]);

    if (!isOpen) return null;

    const handleSave = async () => {
        if (!editDate) {
            setError(t('editModal.errorDate', { defaultValue: 'Bitte ein Datum auswählen.' }));
            return;
        }

        if (!editStartTime || !editEndTime) {
            setError(t('editModal.errorStartEnd'));
            return;
        }

        const startMinutes = convertTimeToMinutes(editStartTime);
        const endMinutes = convertTimeToMinutes(editEndTime);

        if (endMinutes <= startMinutes) {
            setError(t('editModal.errorEndBeforeStart'));
            return;
        }

        if (startMinutes < START_HOUR * 60 || endMinutes > (END_HOUR * 60 + 60)) {
            setError(t('editModal.errorTimeRange', { start: START_HOUR, end: END_HOUR + 1 }));
            return;
        }

        const roundedStart = Math.round(startMinutes / 15) * 15;
        const roundedEnd = Math.round(endMinutes / 15) * 15;

        if (repeatConfig) {
            setShowReplaceDialog(true);
            return;
        }

        onSave(convertMinutesToTime(roundedStart), convertMinutesToTime(roundedEnd), editDate);
        onClose();
    };

    const handleRepeatSave = async (shouldReplace = false) => {
        setShowReplaceDialog(false);
        setIsProcessing(true);

        try {
            if (!companyId || !staffId || !currentUserId) {
                throw new Error(t('errors.missingIds'));
            }

            const roundedStart = Math.round(convertTimeToMinutes(editStartTime) / 15) * 15;
            const roundedEnd = Math.round(convertTimeToMinutes(editEndTime) / 15) * 15;

            const baseShift = {
                date: editDate,
                startTime: convertMinutesToTime(roundedStart),
                endTime: convertMinutesToTime(roundedEnd),
                departmentId: departmentId || '',
                notes: ''
            };

            onSave(convertMinutesToTime(roundedStart), convertMinutesToTime(roundedEnd), editDate);

            await new Promise(resolve => setTimeout(resolve, 500));

            const shiftsRef = ScheduleService.getSchedulesCollection(companyId, staffId);
            const q = query(
                shiftsRef,
                where('date', '==', editDate),
                where('startTime', '==', convertMinutesToTime(roundedStart)),
                where('endTime', '==', convertMinutesToTime(roundedEnd))
            );

            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                throw new Error('Could not find created shift');
            }

            const createdShiftId = snapshot.docs[0].id;

            const count = await ScheduleService.createRepeatedShifts(
                companyId,
                staffId,
                createdShiftId,
                baseShift,
                repeatConfig.recurrence,
                repeatConfig.endDate,
                currentUserId
            );

            showNotification(t('repeatDialog.slotsCreated', { count }), 'success');

            if (onRefresh) {
                await onRefresh();
            }

            onClose();
        } catch (error) {
            console.error('Error creating repeated shifts:', error);
            setError(error.message || t('errors.createRepeatError'));
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDelete = () => {
        onDelete(blockId);
        onClose();
    };

    return (
        <>
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: '16px'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '24px',
                    width: 'min(92vw, 500px)',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    position: 'relative'
                }}>
                    {/* Close button */}
                    <button
                        onClick={onClose}
                        disabled={isProcessing}
                        style={{
                            position: 'absolute',
                            top: '16px',
                            right: '16px',
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            color: '#6b7280',
                            cursor: 'pointer',
                            padding: '4px',
                            lineHeight: 1,
                            transition: 'color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#111827'}
                        onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    >
                        x
                    </button>

                    {/* Modal title */}
                    <div style={{ marginBottom: '20px' }}>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
                            {t('editModal.title')}
                        </h3>
                    </div>

                    <div style={{ marginBottom: '20px' }}>

                        {/* Date picker */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '500',
                                marginBottom: '6px',
                                color: '#374151'
                            }}>
                                {t('editModal.date', { defaultValue: 'Datum' })}
                            </label>
                            <input
                                type="date"
                                value={editDate}
                                onChange={(e) => setEditDate(e.target.value)}
                                disabled={isProcessing}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    cursor: 'pointer'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            />
                        </div>

                        {/* Start time */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '500',
                                marginBottom: '6px',
                                color: '#374151'
                            }}>
                                {t('editModal.startTime')}
                            </label>
                            <input
                                type="time"
                                value={editStartTime}
                                onChange={(e) => setEditStartTime(e.target.value)}
                                disabled={isProcessing}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    outline: 'none'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            />
                        </div>

                        {/* End time */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '500',
                                marginBottom: '6px',
                                color: '#374151'
                            }}>
                                {t('editModal.endTime')}
                            </label>
                            <input
                                type="time"
                                value={editEndTime}
                                onChange={(e) => setEditEndTime(e.target.value)}
                                disabled={isProcessing}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    outline: 'none'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            />
                        </div>

                        {/* Error message */}
                        {error && (
                            <div style={{
                                padding: '8px 12px',
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: '6px',
                                fontSize: '12px',
                                color: '#dc2626'
                            }}>
                                {error}
                            </div>
                        )}
                    </div>

                    <RepeatSection
                        onRepeatChange={setRepeatConfig}
                        initialValues={null}
                    />

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
                        {!isNew && (
                            <button
                                onClick={handleDelete}
                                disabled={isProcessing}
                                style={{
                                    padding: '8px 16px',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    color: 'white',
                                    backgroundColor: '#dc2626',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s',
                                    opacity: isProcessing ? 0.5 : 1
                                }}
                                onMouseEnter={(e) => !isProcessing && (e.target.style.backgroundColor = '#b91c1c')}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
                            >
                                {t('editModal.delete')}
                            </button>
                        )}
                        <button
                            onClick={handleSave}
                            disabled={isProcessing}
                            style={{
                                padding: '8px 16px',
                                fontSize: '13px',
                                fontWeight: '500',
                                color: 'white',
                                backgroundColor: '#3b82f6',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: isProcessing ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s',
                                opacity: isProcessing ? 0.5 : 1
                            }}
                            onMouseEnter={(e) => !isProcessing && (e.target.style.backgroundColor = '#2563eb')}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                        >
                            {isProcessing ? t('editModal.processing') : t('editModal.save')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Repeat confirmation dialog */}
            {showReplaceDialog && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000,
                    padding: '16px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '24px',
                        borderRadius: '12px',
                        width: 'min(92vw, 400px)'
                    }}>
                        <h3 style={{ margin: '0 0 12px 0', fontSize: '18px' }}>
                            {t('repeatDialog.title')}
                        </h3>
                        <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '14px' }}>
                            {t('repeatDialog.description')}
                        </p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => handleRepeatSave(true)}
                                disabled={isProcessing}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    fontSize: '14px',
                                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                                    fontWeight: '500',
                                    background: '#34c759',
                                    color: 'white',
                                    opacity: isProcessing ? 0.5 : 1
                                }}
                            >
                                {t('repeatDialog.confirm')}
                            </button>
                            <button
                                onClick={() => {
                                    setShowReplaceDialog(false);
                                    setRepeatConfig(null);
                                }}
                                disabled={isProcessing}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    fontSize: '14px',
                                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                                    fontWeight: '500',
                                    background: '#e0e0e0',
                                    color: '#333',
                                    opacity: isProcessing ? 0.5 : 1
                                }}
                            >
                                {t('repeatDialog.cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const SchedulePlannerPage = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation('schedule');

    const { currentUser, currentCompany } = useAuth();
    const user = currentUser;
    const companyId = currentCompany?.id;

    // NOTE (§7): aliased to `activeStaff` so archived staff are hidden from the
    // staff selector, department roll-ups, and every other `staff.*` usage
    // below — no other line in this file needs to change.
    const { activeStaff: staff, loading: staffLoading } = useStaff();
    const { departments } = useDepartment();

    const {
        selectedStaff,
        currentDate,
        viewMode,
        dateRange,
        schedules,
        holidays,
        loading,
        errors,
        canManageAllStaff,
        manageableStaffIds,

        setSelectedStaff,
        setCurrentDate,
        setViewMode,
        navigateDate,
        canManageStaff,
        clearError,
        refreshSchedules
    } = useSchedule();
    const { saveWeeklySchedule, loadSchedulesForStaffList } = useScheduleOperations();
    const { showNotification, showConfirm } = useNotification();

    // ========================================================================
    // STATE MANAGEMENT
    // ========================================================================

    const [selectedDepartment, setSelectedDepartment] = useState('all');

    const [selectionState, setSelectionState] = useState({
        isSelecting: false,
        selectionStart: null,
        selectionEnd: null,
        selectedCells: [],
        currentDay: null,
        selections: []
    });

    const [saveState, setSaveState] = useState({
        isSaving: false,
        saveSuccess: false,
        saveError: null
    });

    const [editModalState, setEditModalState] = useState({
        isOpen: false,
        dateISO: null,
        startTime: null,
        endTime: null,
        blockId: null
    });

    const scheduleService = useRef(ScheduleService).current;

    const timeSlots = generateTimeSlots();
    const dayNames = getDayNames(t);

    // ========================================================================
    // FIREBASE DELETION FUNCTION
    // ========================================================================

    const deleteScheduleFromFirebase = async (staffId, dateISO) => {
        if (!companyId) return false;

        try {
            await scheduleService.deleteSchedule(companyId, staffId, dateISO);
            return true;
        } catch (error) {
            console.error('Error deleting schedule from Firebase:', error);
            throw error;
        }
    };

    const loadScheduleData = async () => {
        if (refreshSchedules) {
            await refreshSchedules();
        }
    };

    // ========================================================================
    // EFFECTS
    // ========================================================================

    useEffect(() => {
        if (selectedStaff && schedules[selectedStaff]) {
            let filteredShifts;

            if (viewMode === 'month') {
                const [currentYear, currentMonth] = currentDate.split('-').map(Number);

                filteredShifts = schedules[selectedStaff].filter(shift => {
                    const [shiftYear, shiftMonth] = shift.date.split('-').map(Number);
                    return shiftYear === currentYear && shiftMonth === currentMonth;
                });
            } else {
                filteredShifts = schedules[selectedStaff].filter(shift => {
                    const shiftDate = shift.date;
                    return shiftDate >= dateRange.start && shiftDate <= dateRange.end;
                });
            }

            if (filteredShifts.length > 0) {
                const convertedSelections = convertShiftsToSelections(filteredShifts);

                setSelectionState(prev => ({
                    ...prev,
                    selections: convertedSelections
                }));
            } else {
                setSelectionState(prev => ({
                    ...prev,
                    selections: []
                }));
            }
        } else if (selectedStaff && !schedules[selectedStaff]) {
            setSelectionState(prev => ({
                ...prev,
                selections: []
            }));
        }
    }, [selectedStaff, dateRange.start, dateRange.end, schedules, viewMode, currentDate]);

    useEffect(() => {
        if (saveState.saveSuccess) {
            const timer = setTimeout(() => {
                setSaveState(prev => ({ ...prev, saveSuccess: false }));
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [saveState.saveSuccess]);

    useEffect(() => {
        if (
            selectedDepartment !== 'all' &&
            !selectedStaff &&
            staff.length > 0 &&
            dateRange.start &&
            dateRange.end
        ) {
            const deptStaffIds = staff
                .filter(member =>
                    member.departmentIds &&
                    member.departmentIds.includes(selectedDepartment)
                )
                .map(member => member.id);

            if (deptStaffIds.length > 0) {
                loadSchedulesForStaffList(deptStaffIds);
            }
        }
    }, [selectedDepartment, selectedStaff, staff.length, dateRange.start, dateRange.end]);

    // ========================================================================
    // SELECTION MANAGEMENT FUNCTIONS
    // ========================================================================

    const mergeSelectionsIntoBlocks = useCallback((selections) => {
        if (!selections.length) return [];

        const dayGroups = {};
        selections.forEach(selection => {
            if (!dayGroups[selection.day]) {
                dayGroups[selection.day] = [];
            }
            dayGroups[selection.day].push(selection);
        });

        const blocks = [];
        Object.entries(dayGroups).forEach(([day, daySelections]) => {
            let earliestStartMinutes = 24 * 60;
            let latestEndMinutes = 0;
            const selectionIds = [];

            daySelections.forEach(selection => {
                const startMin = convertTimeToMinutes(selection.startTime);
                const endMin = convertTimeToMinutes(selection.endTime);
                if (startMin < earliestStartMinutes) earliestStartMinutes = startMin;
                if (endMin > latestEndMinutes) latestEndMinutes = endMin;
                selectionIds.push(selection.id);
            });

            const totalDuration = latestEndMinutes - earliestStartMinutes;

            blocks.push({
                id: `block-${day}-${earliestStartMinutes}`,
                day: day,
                startTime: convertMinutesToTime(earliestStartMinutes),
                endTime: convertMinutesToTime(latestEndMinutes),
                duration: totalDuration,
                selections: selectionIds
            });
        });

        return blocks.sort((a, b) => a.day.localeCompare(b.day));
    }, []);

    const selectionBlocks = mergeSelectionsIntoBlocks(selectionState.selections);

    const clearAllSelections = () => {
        setSelectionState({
            isSelecting: false,
            selectionStart: null,
            selectionEnd: null,
            selectedCells: [],
            currentDay: null,
            selections: []
        });
    };

    const deleteDay = async (dateISO) => {
        const daySelections = selectionState.selections.filter(sel => sel.day === dateISO);
        const hasSavedSelections = daySelections.some(sel => sel.isSaved);

        if (hasSavedSelections) {
            try {
                await deleteScheduleFromFirebase(selectedStaff, dateISO);

                setSelectionState(prev => ({
                    ...prev,
                    selections: prev.selections.filter(sel => sel.day !== dateISO)
                }));

                console.log('Day schedule deleted successfully');
            } catch (error) {
                console.error('Error deleting day schedule:', error);
                showNotification(t('errors.deleteDayError'), 'error');
            }
        } else {
            setSelectionState(prev => ({
                ...prev,
                selections: prev.selections.filter(sel => sel.day !== dateISO)
            }));
        }
    };

    const handleEditDay = useCallback((dateISO, startTime, endTime) => {
        const block = selectionBlocks.find(b => b.day === dateISO);

        setEditModalState({
            isOpen: true,
            dateISO,
            startTime,
            endTime,
            blockId: block ? block.id : null
        });
    }, [selectionBlocks]);

    const handleAddShift = () => {
        if (!selectedStaff) return;

        const today = new Date().toISOString().split('T')[0];
        let defaultDate;

        if (viewMode === 'week') {
            defaultDate = (today >= dateRange.start && today <= dateRange.end)
                ? today
                : dateRange.start;
        } else {
            const [currentYear, currentMonth] = currentDate.split('-').map(Number);
            const todayDate = new Date();
            defaultDate = (
                todayDate.getFullYear() === currentYear &&
                (todayDate.getMonth() + 1) === currentMonth
            )
                ? today
                : `${currentDate.slice(0, 7)}-01`;
        }

        setEditModalState({
            isOpen: true,
            dateISO: defaultDate,
            startTime: '09:00',
            endTime: '17:00',
            blockId: null
        });
    };

    const handleSaveEditedTime = useCallback((newStartTime, newEndTime, newDate) => {
        const originalDate = editModalState.dateISO;
        const resolvedDate = newDate || originalDate;

        const startMinutes = convertTimeToMinutes(newStartTime);
        const endMinutes = convertTimeToMinutes(newEndTime);
        const newDuration = endMinutes - startMinutes;

        let baseSelections = selectionState.selections.filter(sel => sel.day !== originalDate);

        if (resolvedDate !== originalDate) {
            baseSelections = baseSelections.filter(
                sel => sel.day !== resolvedDate || sel.isSaved
            );
        }

        const newSelection = {
            id: generateSelectionId(),
            day: resolvedDate,
            startTime: newStartTime,
            endTime: newEndTime,
            duration: newDuration,
            isSaved: false
        };

        setSelectionState(prev => ({
            ...prev,
            selections: [...baseSelections, newSelection]
        }));

        setEditModalState({
            isOpen: false,
            dateISO: null,
            startTime: null,
            endTime: null,
            blockId: null
        });
    }, [editModalState, selectionState.selections]);

    const deleteBlock = async (blockId) => {
        const block = selectionBlocks.find(b => b.id === blockId);
        if (!block) {
            console.warn('Block not found:', blockId);
            return;
        }

        const blockSelections = selectionState.selections.filter(s =>
            block.selections.includes(s.id)
        );

        const hasSavedSelections = blockSelections.some(s => s.isSaved);

        if (!hasSavedSelections) {
            setSelectionState(prev => ({
                ...prev,
                selections: prev.selections.filter(s => !block.selections.includes(s.id))
            }));
            return;
        }

        const firstSelection = blockSelections[0];

        if (firstSelection.batchId) {
            showConfirm(
                t('batchDelete.confirmMessage'),
                async () => {
                    try {
                        const deletedCount = await ScheduleService.deleteFutureBatchShifts(
                            companyId,
                            selectedStaff,
                            firstSelection.batchId,
                            block.day
                        );
                        showNotification(t('batchDelete.slotsDeleted', { count: deletedCount }), 'success');
                    } catch (error) {
                        console.error('Batch delete failed:', error);
                        showNotification(t('errors.deleteRepeatError'), 'error');
                        return;
                    }
                    await loadScheduleData();
                },
                async () => {
                    await deleteScheduleFromFirebase(selectedStaff, block.day);
                    await loadScheduleData();
                },
                {
                    confirmText: t('batchDelete.confirmText'),
                    cancelText: t('batchDelete.cancelText')
                }
            );
        } else {
            await deleteScheduleFromFirebase(selectedStaff, block.day);
            await loadScheduleData();
        }
    };

    const getCurrentSelectionDuration = () => {
        const totalMinutes = selectionState.selectedCells.length * 15;
        return totalMinutes;
    };

    // ========================================================================
    // SCHEDULE DATA FUNCTIONS
    // ========================================================================

    const handleSaveSchedule = async () => {
        if (!selectedStaff || !companyId || selectionState.selections.length === 0) {
            return;
        }

        setSaveState({ isSaving: true, saveSuccess: false, saveError: null });

        try {
            const staffMember = staff.find(s => s.id === selectedStaff);
            if (!staffMember) {
                throw new Error('Staff member not found');
            }

            const shifts = selectionState.selections.map(selection => ({
                date: selection.day,
                startTime: selection.startTime,
                endTime: selection.endTime,
                totalMinutes: selection.duration,
                departmentId: staffMember.primaryDepartmentId || staffMember.departmentIds?.[0] || ''
            }));

            const weekStart = new Date(dateRange.start);
            const weekEnd = new Date(dateRange.end);

            await scheduleService.saveSchedule(
                companyId,
                selectedStaff,
                staffMember.primaryDepartmentId || staffMember.departmentIds?.[0] || '',
                weekStart.toISOString().split('T')[0],
                weekEnd.toISOString().split('T')[0],
                shifts,
                user.uid
            );

            setSaveState({ isSaving: false, saveSuccess: true, saveError: null });

        } catch (error) {
            console.error('Error saving schedule:', error);
            setSaveState({
                isSaving: false,
                saveSuccess: false,
                saveError: error.message
            });
        }
    };

    const getFilteredStaff = () => {
        if (!staff.length) return [];
        let filteredStaff = staff.filter(member => canManageStaff(member.id));

        if (selectedDepartment !== 'all') {
            filteredStaff = filteredStaff.filter(member =>
                member.departmentIds && member.departmentIds.includes(selectedDepartment)
            );
        }

        return filteredStaff.sort((a, b) => a.name.localeCompare(b.name));
    };

    const getStaffTotalMinutesForPeriod = (staffId) => {
        const staffShifts = schedules[staffId] || [];

        if (viewMode === 'month') {
            const [currentYear, currentMonth] = currentDate.split('-').map(Number);
            return staffShifts
                .filter(shift => {
                    const [sy, sm] = shift.date.split('-').map(Number);
                    return sy === currentYear && sm === currentMonth;
                })
                .reduce((total, shift) => {
                    if (shift.totalMinutes) return total + shift.totalMinutes;
                    if (shift.startTime && shift.endTime) {
                        return total + Math.max(0, convertTimeToMinutes(shift.endTime) - convertTimeToMinutes(shift.startTime));
                    }
                    return total;
                }, 0);
        }

        if (!dateRange.start || !dateRange.end) return 0;

        return staffShifts
            .filter(shift => shift.date >= dateRange.start && shift.date <= dateRange.end)
            .reduce((total, shift) => {
                if (shift.totalMinutes) return total + shift.totalMinutes;
                if (shift.startTime && shift.endTime) {
                    return total + Math.max(0, convertTimeToMinutes(shift.endTime) - convertTimeToMinutes(shift.startTime));
                }
                return total;
            }, 0);
    };

    const getDepartmentStaff = () => {
        if (selectedDepartment === 'all' || !selectedDepartment || !staff.length) return [];
        return staff
            .filter(member =>
                member.departmentIds &&
                member.departmentIds.includes(selectedDepartment) &&
                getStaffTotalMinutesForPeriod(member.id) > 0
            )
            .sort((a, b) => a.name.localeCompare(b.name));
    };

    const getAvailableDepartments = () => {
        if (!staff.length) return [];
        const manageableStaff = staff.filter(member => canManageStaff(member.id));
        const departmentIds = new Set();

        manageableStaff.forEach(member => {
            if (member.departmentIds) {
                member.departmentIds.forEach(deptId => departmentIds.add(deptId));
            }
        });

        return Array.from(departmentIds).map(id => {
            const department = departments.find(dept => dept.id === id);
            return {
                id,
                name: department ? department.name : t('controls.departmentFallback', { id: id.slice(-8) })
            };
        });
    };

    const formatCurrentDateDisplay = () => {
        const date = new Date(currentDate);
        const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';

        if (viewMode === 'week') {
            const weekStart = new Date(date);
            weekStart.setDate(date.getDate() - date.getDay() + 1);
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);

            return `${weekStart.toLocaleDateString(locale, {
                day: '2-digit',
                month: '2-digit'
            })} - ${weekEnd.toLocaleDateString(locale, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            })}`;
        }

        return date.toLocaleDateString(locale, {
            month: 'long',
            year: 'numeric'
        });
    };

    const getSelectedStaffInfo = () => {
        if (!selectedStaff) return null;
        const staffMember = staff.find(s => s.id === selectedStaff);
        return staffMember ? {
            id: selectedStaff,
            name: staffMember.name
        } : null;
    };

    const getWeekDays = () => {
        return Array.from({ length: 7 }, (_, dayIndex) => {
            const date = new Date(dateRange.start);
            date.setDate(date.getDate() + dayIndex);
            const dateString = date.toISOString().split('T')[0];
            const isHoliday = holidays.some(h => h.dateString === dateString);

            return {
                dayIndex,
                name: dayNames[dayIndex],
                date: date.getDate(),
                month: date.getMonth() + 1,
                dateString,
                isHoliday,
                displayDate: `${date.getDate()}.${(date.getMonth() + 1).toString().padStart(2, '0')}`
            };
        });
    };

    const getExistingShifts = () => {
        if (!selectedStaff || !schedules[selectedStaff]) return [];
        return schedules[selectedStaff].filter(shift => {
            const shiftDate = shift.date;
            return shiftDate >= dateRange.start && shiftDate <= dateRange.end;
        });
    };

    const isSlotOccupied = (day, time) => {
        const existingShifts = getExistingShifts();
        const timeInMinutes = convertTimeToMinutes(time);

        const occupiedByShift = existingShifts.some(shift => {
            if (shift.date !== day) return false;

            let startMinutes, endMinutes;

            if (shift.startTime && shift.endTime) {
                startMinutes = convertTimeToMinutes(shift.startTime);
                endMinutes = convertTimeToMinutes(shift.endTime);
            } else if (typeof shift.startHour === 'number' && typeof shift.startMinute === 'number') {
                startMinutes = shift.startHour * 60 + shift.startMinute;
                endMinutes = shift.endHour * 60 + shift.endMinute;
            } else {
                console.warn('Unknown shift format:', shift);
                return false;
            }

            return timeInMinutes >= startMinutes && timeInMinutes < endMinutes;
        });

        if (occupiedByShift) return true;

        const occupiedBySelection = selectionBlocks.some(block => {
            if (block.day !== day) return false;
            const startTime = convertTimeToMinutes(block.startTime);
            const endTime = convertTimeToMinutes(block.endTime);
            return timeInMinutes >= startTime && timeInMinutes < endTime;
        });

        return occupiedBySelection;
    };

    const getSelectionBetween = (start, end, day) => {
        if (!start || !end) return [];

        const startMinutes = convertTimeToMinutes(start);
        const endMinutes = convertTimeToMinutes(end);
        const minMinutes = Math.min(startMinutes, endMinutes);
        const maxMinutes = Math.max(startMinutes, endMinutes);

        const MIN_TIME = 7 * 60;
        const MAX_TIME = 19 * 60;

        const selection = [];
        const clampedMin = Math.max(minMinutes, MIN_TIME);
        const clampedMax = Math.min(maxMinutes, MAX_TIME);

        for (let minutes = clampedMin; minutes < clampedMax; minutes += 15) {
            const timeStr = convertMinutesToTime(minutes);
            selection.push(`${day}-${timeStr}`);
        }

        return selection;
    };

    const isCellInCurrentSelection = (day, time) => {
        const cellKey = `${day}-${time}`;
        return selectionState.selectedCells.includes(cellKey);
    };

    const isCellInSelectionBlock = (day, time) => {
        const timeInMinutes = convertTimeToMinutes(time);
        return selectionBlocks.some(block => {
            if (block.day !== day) return false;
            const startTime = convertTimeToMinutes(block.startTime);
            const endTime = convertTimeToMinutes(block.endTime);
            return timeInMinutes >= startTime && timeInMinutes < endTime;
        });
    };

    const getBlockForCell = (day, time) => {
        const timeInMinutes = convertTimeToMinutes(time);
        return selectionBlocks.find(block => {
            if (block.day !== day) return false;
            const startTime = convertTimeToMinutes(block.startTime);
            const endTime = convertTimeToMinutes(block.endTime);
            return timeInMinutes >= startTime && timeInMinutes < endTime;
        });
    };

    const shouldShowBlockHeader = (day, time) => {
        const block = getBlockForCell(day, time);
        if (!block) return false;

        return convertTimeToMinutes(time) === convertTimeToMinutes(block.startTime);
    };

    // ========================================================================
    // MOUSE EVENT HANDLERS
    // ========================================================================

    const handleMouseDown = useCallback((e, day, timeSlot) => {
        e.preventDefault();
        if (!selectedStaff) return;

        const occupied = isSlotOccupied(day.dateString, timeSlot.timeString);

        if (occupied) {
            const block = selectionBlocks.find(b => {
                if (b.day !== day.dateString) return false;
                const timeInMinutes = convertTimeToMinutes(timeSlot.timeString);
                const startTime = convertTimeToMinutes(b.startTime);
                const endTime = convertTimeToMinutes(b.endTime);
                return timeInMinutes >= startTime && timeInMinutes < endTime;
            });

            if (block) {
                setEditModalState({
                    isOpen: true,
                    dateISO: block.day,
                    startTime: block.startTime,
                    endTime: block.endTime,
                    blockId: block.id
                });
                return;
            }
            return;
        }

        const cellKey = `${day.dateString}-${timeSlot.timeString}`;
        setSelectionState(prev => ({
            ...prev,
            isSelecting: true,
            selectionStart: timeSlot.timeString,
            selectionEnd: timeSlot.timeString,
            selectedCells: [cellKey],
            currentDay: day.dateString
        }));
    }, [selectedStaff, selectionBlocks]);

    const handleMouseEnter = useCallback((e, day, timeSlot) => {
        if (selectionState.isSelecting && selectionState.selectionStart && day.dateString === selectionState.currentDay) {
            const hoveredTime = timeSlot.timeString;
            const selection = getSelectionBetween(selectionState.selectionStart, hoveredTime, day.dateString);

            const canSelect = selection.every(cellKey => {
                const [cellDay, cellTime] = cellKey.split('-');
                const existingShifts = getExistingShifts();
                const timeInMinutes = convertTimeToMinutes(cellTime);

                return !existingShifts.some(shift => {
                    if (shift.date !== cellDay) return false;

                    let startMinutes, endMinutes;
                    if (shift.startTime && shift.endTime) {
                        startMinutes = convertTimeToMinutes(shift.startTime);
                        endMinutes = convertTimeToMinutes(shift.endTime);
                    } else if (typeof shift.startHour === 'number' && typeof shift.startMinute === 'number') {
                        startMinutes = shift.startHour * 60 + shift.startMinute;
                        endMinutes = shift.endHour * 60 + shift.endMinute;
                    } else {
                        return false;
                    }

                    return timeInMinutes >= startMinutes && timeInMinutes < endMinutes;
                });
            });

            if (canSelect) {
                setSelectionState(prev => ({
                    ...prev,
                    selectionEnd: hoveredTime,
                    selectedCells: selection
                }));
            }
        }
    }, [selectionState.isSelecting, selectionState.selectionStart, selectionState.currentDay]);

    const handleMouseUp = useCallback((e, day, timeSlot) => {
        e.preventDefault();

        if (selectionState.isSelecting && selectionState.selectedCells.length > 0) {
            const duration = selectionState.selectedCells.length * 15;
            const startMinutes = convertTimeToMinutes(selectionState.selectionStart);
            const finalStartTime = convertMinutesToTime(startMinutes);
            const finalEndTime = convertMinutesToTime(startMinutes + duration);

            const newSelection = {
                id: generateSelectionId(),
                day: selectionState.currentDay,
                startTime: finalStartTime,
                endTime: finalEndTime,
                duration: duration,
                isSaved: false
            };

            setSelectionState(prev => ({
                ...prev,
                isSelecting: false,
                selectionStart: null,
                selectionEnd: null,
                selectedCells: [],
                currentDay: null,
                selections: [...prev.selections, newSelection]
            }));
        }
    }, [selectionState]);

    const selectionStateRef = useRef(selectionState);
    useEffect(() => {
        selectionStateRef.current = selectionState;
    }, [selectionState]);

    useEffect(() => {
        const handleGlobalMouseUp = (e) => {
            if (selectionStateRef.current.isSelecting && selectionStateRef.current.selectedCells.length > 0) {
                const state = selectionStateRef.current;
                const duration = state.selectedCells.length * 15;
                const startMinutes = convertTimeToMinutes(state.selectionStart);
                const finalStartTime = convertMinutesToTime(startMinutes);
                const finalEndTime = convertMinutesToTime(startMinutes + duration);

                const newSelection = {
                    id: generateSelectionId(),
                    day: state.currentDay,
                    startTime: finalStartTime,
                    endTime: finalEndTime,
                    duration: duration,
                    isSaved: false
                };

                setSelectionState(prev => ({
                    ...prev,
                    isSelecting: false,
                    selectionStart: null,
                    selectionEnd: null,
                    selectedCells: [],
                    currentDay: null,
                    selections: [...prev.selections, newSelection]
                }));
            }
        };

        document.addEventListener('mouseup', handleGlobalMouseUp);
        return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
    }, []);

    // ========================================================================
    // RENDER DATA PREPARATION
    // ========================================================================

    const filteredStaff = getFilteredStaff();
    const availableDepartments = getAvailableDepartments();
    const selectedStaffInfo = getSelectedStaffInfo();
    const weekDays = getWeekDays();
    const currentSelectionDuration = getCurrentSelectionDuration();
    const departmentStaff = getDepartmentStaff();
    const isDepartmentOverviewMode = selectedDepartment !== 'all' && !selectedStaff && departmentStaff.length > 0;

    // ========================================================================
    // LOADING STATE
    // ========================================================================

    if (staffLoading.staff || loading.schedules) {
        return (
            <div className="schedule-planner">
                <div className="schedule-planner__loading">
                    <div className="loading-spinner">
                        <div className="spinner"></div>
                        <p>{t('planner.loading')}</p>
                    </div>
                </div>
            </div>
        );
    }

    // ========================================================================
    // RENDER
    // ========================================================================

    return (
        <div className="schedule-planner" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
            <TimeEditModal
                isOpen={editModalState.isOpen}
                onClose={() => setEditModalState({ isOpen: false, dateISO: null, startTime: null, endTime: null, blockId: null })}
                dateISO={editModalState.dateISO}
                startTime={editModalState.startTime}
                endTime={editModalState.endTime}
                blockId={editModalState.blockId}
                onSave={handleSaveEditedTime}
                onDelete={deleteBlock}
                staffId={selectedStaff}
                companyId={companyId}
                currentUserId={user?.uid}
                onRefresh={refreshSchedules}
                isNew={!editModalState.blockId}
                departmentId={
                    staff.find(s => s.id === selectedStaff)?.primaryDepartmentId ||
                    staff.find(s => s.id === selectedStaff)?.departmentIds?.[0] ||
                    ''
                }
            />

            {/* HEADER */}
            <header className="planner-header" style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                <div className="planner-header__content">
                    <div className="planner-header__title" style={{ marginBottom: '8px' }}>
                        <h1 style={{ margin: 0, fontSize: '20px' }}>{t('planner.title')}</h1>
                        <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>{t('planner.subtitle')}</p>
                    </div>

                    <div className="planner-header__controls" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>

                        <div className="staff-selection-group" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {availableDepartments.length > 1 && (
                                <select
                                    className="header-select header-select--department"
                                    value={selectedDepartment}
                                    onChange={(e) => setSelectedDepartment(e.target.value)}
                                    style={{ padding: '4px 8px', fontSize: '13px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                                >
                                    <option value="all">{t('controls.allDepartments')}</option>
                                    {availableDepartments.map(dept => (
                                        <option key={dept.id} value={dept.id}>
                                            {dept.name}
                                        </option>
                                    ))}
                                </select>
                            )}

                            <select
                                className="header-select header-select--staff"
                                value={selectedStaff || ''}
                                onChange={(e) => setSelectedStaff(e.target.value)}
                                style={{ padding: '4px 8px', fontSize: '13px', borderRadius: '6px', border: '1px solid #d1d5db', minWidth: '150px' }}
                            >
                                <option value="">{t('controls.selectStaff')}</option>
                                {filteredStaff.map(staffMember => (
                                    <option key={staffMember.id} value={staffMember.id}>
                                        {staffMember.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="view-toggle" style={{ display: 'flex', background: '#f3f4f6', borderRadius: '6px', padding: '2px' }}>
                            <button
                                className={`view-toggle__btn ${viewMode === 'week' ? 'active' : ''}`}
                                onClick={() => setViewMode('week')}
                                style={{
                                    padding: '4px 12px',
                                    fontSize: '12px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    background: viewMode === 'week' ? 'white' : 'transparent',
                                    cursor: 'pointer'
                                }}
                            >
                                {t('controls.week')}
                            </button>
                            <button
                                className={`view-toggle__btn ${viewMode === 'month' ? 'active' : ''}`}
                                onClick={() => setViewMode('month')}
                                style={{
                                    padding: '4px 12px',
                                    fontSize: '12px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    background: viewMode === 'month' ? 'white' : 'transparent',
                                    cursor: 'pointer'
                                }}
                            >
                                {t('controls.month')}
                            </button>
                        </div>

                        <div className="date-navigation" style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <button
                                className="date-nav__btn"
                                onClick={() => navigateDate('prev')}
                                style={{ padding: '4px 8px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', background: 'white' }}
                            >
                                &larr;
                            </button>

                            <div className="date-nav__current" style={{ padding: '4px 12px', fontSize: '12px', fontWeight: '500', minWidth: '140px', textAlign: 'center' }}>
                                {formatCurrentDateDisplay()}
                            </div>

                            <button
                                className="date-nav__btn"
                                onClick={() => navigateDate('next')}
                                style={{ padding: '4px 8px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', background: 'white' }}
                            >
                                &rarr;
                            </button>

                            <button
                                className="date-nav__today"
                                onClick={() => setCurrentDate(new Date().toISOString().split('T')[0])}
                                style={{ padding: '4px 12px', fontSize: '12px', border: '1px solid #3b82f6', borderRadius: '4px', cursor: 'pointer', background: 'white', color: '#3b82f6', marginLeft: '4px' }}
                            >
                                {t('controls.today')}
                            </button>

                            <button
                                onClick={handleAddShift}
                                disabled={!selectedStaff}
                                title={!selectedStaff ? t('controls.selectStaffFirst') : t('controls.addShift')}
                                style={{
                                    padding: '4px 10px',
                                    fontSize: '16px',
                                    fontWeight: '700',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: selectedStaff ? 'pointer' : 'not-allowed',
                                    background: selectedStaff ? '#3b82f6' : '#9ca3af',
                                    color: 'white',
                                    marginLeft: '4px',
                                    lineHeight: 1,
                                    transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => { if (selectedStaff) e.target.style.background = '#2563eb'; }}
                                onMouseLeave={(e) => { e.target.style.background = selectedStaff ? '#3b82f6' : '#9ca3af'; }}
                            >
                                +
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* CURRENT DRAG SELECTION DISPLAY */}
            {selectionState.isSelecting && selectionState.selectedCells.length > 0 && (
                <div style={{
                    position: 'absolute',
                    top: '80px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '16px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    zIndex: 1000,
                    fontWeight: 'bold',
                    fontSize: '12px',
                    whiteSpace: 'nowrap'
                }}>
                    Total {formatDuration(currentSelectionDuration)}
                </div>
            )}

            {/* ERROR DISPLAYS */}
            {Object.entries(errors).map(([key, error]) => error && (
                <div key={key} className="error-banner" style={{ padding: '8px', background: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
                    <div className="error-banner__content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span className="error-banner__message" style={{ fontSize: '12px', color: '#dc2626' }}>{error}</span>
                        <button
                            className="error-banner__close"
                            onClick={() => clearError(key)}
                            style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '18px', color: '#dc2626' }}
                        >
                            x
                        </button>
                    </div>
                </div>
            ))}

            {saveState.saveError && (
                <div className="error-banner" style={{ padding: '8px', background: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
                    <div className="error-banner__content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span className="error-banner__message" style={{ fontSize: '12px', color: '#dc2626' }}>
                            {t('errors.savingError')}{saveState.saveError}
                        </span>
                        <button
                            className="error-banner__close"
                            onClick={() => setSaveState(prev => ({ ...prev, saveError: null }))}
                            style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '18px', color: '#dc2626' }}
                        >
                            x
                        </button>
                    </div>
                </div>
            )}

            {/* MAIN CONTENT */}
            <div className="planner-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '0 12px' }}>
                {isDepartmentOverviewMode ? (
                    <div className="schedule-grid-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', marginTop: '8px' }}>
                        <DepartmentOverviewCalendar
                            departmentStaff={departmentStaff}
                            schedules={schedules}
                            currentDate={currentDate}
                            viewMode={viewMode}
                            holidays={holidays}
                            dateRange={dateRange}
                        />
                    </div>
                ) : !selectedStaffInfo ? (
                    <div className="calendar-empty" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div className="empty-state" style={{ textAlign: 'center' }}>
                            <div className="empty-state__icon" style={{ fontSize: '48px', marginBottom: '16px' }}>
                            </div>
                            <h3 className="empty-state__title" style={{ margin: '0 0 8px 0', fontSize: '18px' }}>
                                {t('emptyState.title')}
                            </h3>
                            <p className="empty-state__text" style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                                {t('emptyState.description')}
                            </p>
                        </div>
                    </div>
                ) : viewMode === 'month' ? (
                    <div className="schedule-grid-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', marginTop: '8px' }}>
                        <MonthlyCalendarView
                            selectedStaffInfo={selectedStaffInfo}
                            currentDate={currentDate}
                            selections={selectionState.selections.filter(sel => {
                                const [currentYear, currentMonth] = currentDate.split('-');
                                const [selYear, selMonth] = sel.day.split('-');
                                return currentYear === selYear && currentMonth === selMonth;
                            })}
                            holidays={holidays}
                            onCellClick={(dateString) => {
                                setCurrentDate(dateString);
                                setViewMode('week');
                            }}
                            selectedDepartmentId={staff.find(s => s.id === selectedStaff)?.primaryDepartmentId || staff.find(s => s.id === selectedStaff)?.departmentIds?.[0]}
                        />
                    </div>
                ) : (
                    // WEEK VIEW
                    <div
                        className="schedule-grid-container"
                        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', marginTop: '8px' }}
                    >
                        <div
                            className="schedule-week-scroll-wrapper"
                            style={{
                                flex: 1,
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0
                            }}
                        >
                            <div
                                style={{
                                    minWidth: '600px',
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minHeight: 0
                                }}
                            >
                                {/*
                                  Grid column header.
                                  display:grid and grid-template-columns are now owned by
                                  .grid-header in schedule-planner-layout.css so that the
                                  1200px and 480px media queries can override them.
                                */}
                                <div className="grid-header">
                                    <div
                                        className="time-header"
                                        style={{ fontSize: '11px', fontWeight: '600', padding: '4px', color: 'white' }}
                                    >
                                        {t('controls.time')}
                                    </div>
                                    {weekDays.map((day) => (
                                        <div
                                            key={day.dayIndex}
                                            className={`day-header ${day.isHoliday ? 'holiday' : ''}`}
                                            style={{
                                                textAlign: 'center',
                                                padding: '4px',
                                                ...(day.isHoliday && {
                                                    backgroundColor: 'rgba(109, 40, 217, 0.5)',
                                                    color: 'white',
                                                    transition: 'none'
                                                })
                                            }}
                                        >
                                            <div className="day-name" style={{ fontSize: '12px', fontWeight: '600' }}>{day.name}</div>
                                            <div className="day-date" style={{ fontSize: '10px', color: 'white', opacity: 1 }}>{day.displayDate}</div>
                                        </div>
                                    ))}
                                </div>

                                {/* Grid body */}
                                <div
                                    className="grid-body"
                                    style={{
                                        flex: 1,
                                        overflowY: 'auto',
                                        overflowX: 'visible',
                                        paddingTop: '8px',
                                        paddingBottom: '8px',
                                        userSelect: 'none',
                                        WebkitUserSelect: 'none',
                                        MozUserSelect: 'none'
                                    }}
                                >
                                    {timeSlots.map((timeSlot) => {
                                        const isHourStart = timeSlot.minute === 0;
                                        const isHalfHour = timeSlot.minute === 30;

                                        return (
                                            /*
                                              display:grid, grid-template-columns, and min-height
                                              are now owned by .grid-row in
                                              schedule-planner-layout.css.
                                              The conditional hour-start border is applied via the
                                              .grid-row--hour-start modifier class so no inline
                                              style is needed here.
                                            */
                                            <div
                                                key={timeSlot.slotIndex}
                                                className={`grid-row${isHourStart ? ' grid-row--hour-start' : ''}`}
                                            >
                                                <div className={`time-slot ${isHourStart ? 'full-hour' : ''}`} style={{
                                                    fontSize: isHourStart ? '13px' : '9px',
                                                    color: isHourStart ? '#111827' : 'transparent',
                                                    padding: '4px 8px',
                                                    borderRight: '1px solid #e5e7eb',
                                                    fontWeight: isHourStart ? '700' : '400',
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    minHeight: '20px',
                                                    backgroundColor: isHourStart ? '#f9fafb' : 'transparent',
                                                    position: 'relative'
                                                }}>
                                                    {isHourStart && (
                                                        <span style={{ position: 'sticky', top: 0, paddingTop: '2px' }}>
                                                            {timeSlot.timeString}
                                                        </span>
                                                    )}

                                                    {!isHourStart && isHalfHour && (
                                                        <span style={{
                                                            width: '3px',
                                                            height: '3px',
                                                            backgroundColor: '#d1d5db',
                                                            borderRadius: '50%',
                                                            marginLeft: '4px',
                                                            marginTop: '8px'
                                                        }}></span>
                                                    )}
                                                </div>

                                                {weekDays.map((day) => {
                                                    const isCurrentSelection = isCellInCurrentSelection(day.dateString, timeSlot.timeString);
                                                    const isInBlock = isCellInSelectionBlock(day.dateString, timeSlot.timeString);

                                                    const block = getBlockForCell(day.dateString, timeSlot.timeString);
                                                    const showBlockHeader = shouldShowBlockHeader(day.dateString, timeSlot.timeString);

                                                    const hourGroup = Math.floor(timeSlot.hour - START_HOUR);
                                                    const isEvenHour = hourGroup % 2 === 0;

                                                    return (
                                                        <div
                                                            key={`${day.dateString}-${timeSlot.timeString}`}
                                                            className={`schedule-cell ${day.isHoliday ? 'holiday-cell' : ''}`}
                                                            data-day={day.name}
                                                            data-date={day.dateString}
                                                            data-time={timeSlot.timeString}
                                                            onMouseDown={(e) => handleMouseDown(e, day, timeSlot)}
                                                            onMouseEnter={(e) => handleMouseEnter(e, day, timeSlot)}
                                                            onMouseUp={(e) => handleMouseUp(e, day, timeSlot)}
                                                            style={{
                                                                userSelect: 'none',
                                                                cursor: isInBlock ? 'pointer' : 'crosshair',
                                                                backgroundColor: isCurrentSelection
                                                                    ? 'rgba(59, 130, 246, 0.8)'
                                                                    : (isInBlock
                                                                        ? 'rgba(59, 130, 246, 0.4)'
                                                                        : (day.isHoliday
                                                                            ? 'rgba(139, 92, 246, 0.1)'
                                                                            : (isEvenHour ? '#ffffff' : '#f9fafb'))),
                                                                borderRight: '1px solid rgba(229, 231, 235, 0.5)',
                                                                borderBottom: timeSlot.minute === 45 ? '1px solid rgba(209, 213, 219, 0.8)' : '1px solid rgba(229, 231, 235, 0.3)',
                                                                minHeight: '20px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                position: 'relative',
                                                                overflow: 'visible',
                                                                transition: 'background-color 0.1s ease'
                                                            }}
                                                        >
                                                            {showBlockHeader && block && (
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    top: '2px',
                                                                    left: '4px',
                                                                    background: 'rgba(59, 130, 246, 0.95)',
                                                                    color: 'white',
                                                                    fontSize: '10px',
                                                                    fontWeight: '600',
                                                                    padding: '2px 6px',
                                                                    borderRadius: '10px',
                                                                    whiteSpace: 'nowrap',
                                                                    zIndex: 51,
                                                                    lineHeight: 1.2
                                                                }}>
                                                                    {formatDuration(block.duration)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* BOTTOM PANEL */}
            <ScheduleSelectionPanel
                selectedStaffInfo={selectedStaffInfo}
                selections={selectionState.selections}
                selectionBlocks={selectionBlocks}
                onDeleteDay={deleteDay}
                onEditDay={handleEditDay}
                onClearAll={clearAllSelections}
                onSave={handleSaveSchedule}
                isSaving={saveState.isSaving}
                saveSuccess={saveState.saveSuccess}
            />
        </div>
    );
};

export default SchedulePlannerPage;